/**
 * use-drivers — TanStack Query hook for the active driver roster.
 *
 * Phase 9 / Plan 09-04 — sources the driver roster from GET /v1/drivers.
 * This is the SINGLE source for BOTH the BulkAssignBar picker AND the
 * ForceAssignModal driver selector (Blocker 4 — do NOT derive from job.driverId set).
 * Including this endpoint means idle drivers with no current jobs are selectable.
 *
 * No 30s poll needed — the roster changes infrequently (drivers go on/off shift).
 * staleTime: 60_000 means the roster is re-fetched at most once per minute.
 *
 * Spec: DISPATCH-01 (Blocker 4 — full roster incl. idle drivers); DISPATCH-05.
 */

import { useApiQuery, useApiOpts } from '@lodex-pro/web-shared';

/**
 * Active driver roster row for assign / force-assign pickers (DISPATCH-01/05).
 * Mirrors DriverSummary from @lodex-pro/shared/src/types/dispatch.ts.
 * Defined locally to avoid Vite build-time resolution of the @lodex-pro/shared package entry.
 */
export interface DriverSummary {
  readonly driverId: string;
  readonly driverName: string;
  // Wave 3 Part 2 — dispatch-board grouping/filtering. Defaults 'DAY' / null.
  readonly shift: string;
  readonly homeYardId: string | null;
  readonly yardName: string | null;
  // Quick 260617-map (#5) — current truck unit number (open shift), for the board card.
  readonly truckNumber?: string | null;
  // OPEN-027 prong 3 — whether the driver has an OPEN shift. truckNumber alone could
  // not say: it is null both for "no shift" and for "on shift, no truck picked", so the
  // case the mandatory shift gate exists to prevent looked identical to a normal one.
  // Optional, and absent means UNKNOWN rather than off-shift — an older payload must
  // not make every driver render as off shift.
  readonly hasOpenShift?: boolean;
  // OPEN-027 follow-up — the open shift's active trailer. Null = no trailer (bobtail
  // is legitimate); absent (older payload) = UNKNOWN, so no NO TRAILER marker.
  readonly trailerNumber?: string | null;
  // OPEN-027 — the open shift's id, for linking. Null when not on shift.
  readonly openShiftId?: string | null;
}

/** Response envelope for GET /v1/drivers. */
export interface DriversResponse {
  readonly drivers: readonly DriverSummary[];
}

/**
 * Stable array query key for the drivers roster.
 * Using a tuple prevents cache collisions with other feature hooks (RESEARCH Pitfall 4).
 */
export const DRIVERS_QUERY_KEY = ['drivers', 'roster'] as const;

/**
 * Fetches the active driver/lead-driver roster for the current tenant.
 * Includes idle drivers with no current jobs — the correct source for assign pickers.
 *
 * @param apiOpts  API configuration — from `useApiOpts()`.
 */
export function useDrivers(
  apiOpts: ReturnType<typeof useApiOpts>,
): ReturnType<typeof useApiQuery<DriversResponse>> {
  return useApiQuery<DriversResponse>(apiOpts, '/v1/drivers', {
    queryKey: DRIVERS_QUERY_KEY,
    staleTime: 60_000,
  });
}
